import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { PaginatedResponse } from '@brickup/shared/dto'
import { OFFSET_PAGINATION_OPTIONS } from '@/helpers/pagination'

export type OffsetInfiniteList<TItem, TPage> = {
  items: TItem[]
  /** Rows matching the current filters, across all pages. */
  total: number
  /** Rows without any filter, where the endpoint reports it. */
  unfilteredTotal: number | undefined
  /**
   * The first page as the endpoint returned it, for responses carrying
   * whole-table figures next to the rows. Reading them here keeps them on the
   * request that is being made anyway.
   */
  firstPage: TPage | undefined
  isLoading: boolean
  /** The list could not be fetched — said above the rows, so a failed refetch
   *  does not blank a table that still holds the last good page. */
  isError: boolean
  fetchNextPage: () => void
  hasMore: boolean
  isLoadingMore: boolean
}

/** The row type of a page, so callers only ever name the response type. */
type PageItem<TPage> =
  TPage extends PaginatedResponse<infer TItem> ? TItem : never

/**
 * An infinite query refetches every page it has loaded, sequentially. Without
 * a stale window, tabbing back into a list scrolled five pages deep costs five
 * round trips. Mutations invalidate their list key regardless.
 */
const DEFAULT_STALE_TIME = 60 * 1000

/**
 * One page-at-a-time list backed by an endpoint returning `PaginatedResponse`.
 * Flattens the pages and surfaces the counts, so screens deal in rows rather
 * than in pages.
 */
export function useOffsetInfiniteList<TPage extends PaginatedResponse<any>>({
  queryKey,
  fetchPage,
  pageSize,
  enabled,
  staleTime,
}: {
  queryKey: readonly unknown[]
  fetchPage: (page: { skip: number; take: number }) => Promise<TPage>
  pageSize: number
  enabled?: boolean
  staleTime?: number
}): OffsetInfiniteList<PageItem<TPage>, TPage> {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      fetchPage({ skip: pageParam, take: pageSize }),
    enabled,
    staleTime: staleTime ?? DEFAULT_STALE_TIME,
    // Changing a filter starts a new query. Holding the previous rows keeps
    // the table in place instead of collapsing it to a skeleton between
    // keystrokes of a search term.
    placeholderData: keepPreviousData,
    ...OFFSET_PAGINATION_OPTIONS,
  })

  const items = useMemo(
    () => query.data?.pages.flatMap(page => page.data) ?? [],
    [query.data],
  )

  const firstPage = query.data?.pages[0]

  return {
    items,
    total: firstPage?.meta.total ?? 0,
    unfilteredTotal: firstPage?.meta.unfilteredTotal,
    firstPage,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasMore: !!query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
  }
}
